/**
 * International dial codes shared by the web `PhoneInput` (profile phone verification + booking guest
 * phone) and the mobile `PhoneVerificationSheet` country-code picker (ELARA-730). One source so the two
 * platforms can't drift. Deliberately curated — Elara's members are mostly US/CA/UK, plus staff and the
 * booking destinations we see — rather than the full ITU list.
 *
 * **Adding a country:** append one line to {@link CURATED_DIAL_CODES}. The picker order is computed
 * ({@link PINNED_DIAL_CODE_ISOS} first, then alphabetical by name), so where the line sits in the literal
 * doesn't matter. The dial code must be unique in the list: both pickers key their selection by dial code,
 * so a second entry sharing one (e.g. a separate Canada row on +1) would collide with the first — the
 * uniqueness test in `dial-codes.test.ts` rejects it.
 *
 * `iso` is the primary ISO-3166 alpha-2 (used to derive the flag emoji and to default the picker from
 * the user's profile-address country); `code` is the E.164 dial code; `name` is the picker label on both
 * platforms; `placeholder` is an example national number shown for that region where useful.
 */
export interface DialCode {
  iso: string;
  code: string;
  name: string;
  placeholder?: string;
}

/** The source list, kept alphabetical for easy scanning — but see {@link DIAL_CODES} for the picker order. */
const CURATED_DIAL_CODES: readonly DialCode[] = [
  { iso: 'AU', code: '+61', name: 'Australia' },
  { iso: 'AT', code: '+43', name: 'Austria' },
  { iso: 'BR', code: '+55', name: 'Brazil' },
  { iso: 'CN', code: '+86', name: 'China' },
  { iso: 'CZ', code: '+420', name: 'Czechia' },
  { iso: 'DK', code: '+45', name: 'Denmark' },
  { iso: 'EG', code: '+20', name: 'Egypt' },
  { iso: 'FI', code: '+358', name: 'Finland' },
  { iso: 'FR', code: '+33', name: 'France' },
  { iso: 'DE', code: '+49', name: 'Germany' },
  { iso: 'GR', code: '+30', name: 'Greece' },
  { iso: 'HK', code: '+852', name: 'Hong Kong' },
  { iso: 'HU', code: '+36', name: 'Hungary' },
  { iso: 'IS', code: '+354', name: 'Iceland' },
  { iso: 'IN', code: '+91', name: 'India' },
  { iso: 'IE', code: '+353', name: 'Ireland' },
  { iso: 'IT', code: '+39', name: 'Italy' },
  { iso: 'JP', code: '+81', name: 'Japan' },
  { iso: 'LU', code: '+352', name: 'Luxembourg' },
  { iso: 'MX', code: '+52', name: 'Mexico' },
  { iso: 'NL', code: '+31', name: 'Netherlands' },
  { iso: 'NZ', code: '+64', name: 'New Zealand' },
  { iso: 'NO', code: '+47', name: 'Norway' },
  { iso: 'PT', code: '+351', name: 'Portugal' },
  { iso: 'RU', code: '+7', name: 'Russia' },
  { iso: 'RS', code: '+381', name: 'Serbia' },
  { iso: 'SG', code: '+65', name: 'Singapore' },
  { iso: 'ZA', code: '+27', name: 'South Africa' },
  { iso: 'KR', code: '+82', name: 'South Korea' },
  { iso: 'ES', code: '+34', name: 'Spain' },
  { iso: 'SE', code: '+46', name: 'Sweden' },
  { iso: 'CH', code: '+41', name: 'Switzerland' },
  { iso: 'TW', code: '+886', name: 'Taiwan' },
  { iso: 'TH', code: '+66', name: 'Thailand' },
  { iso: 'TR', code: '+90', name: 'Turkey' },
  { iso: 'AE', code: '+971', name: 'United Arab Emirates' },
  { iso: 'GB', code: '+44', name: 'United Kingdom', placeholder: '00000 000000' },
  { iso: 'US', code: '+1', name: 'United States / Canada', placeholder: '(000) 000-0000' },
];

/**
 * ISO codes pinned to the top of the picker, in this order — where most Elara members are. Everything
 * else follows alphabetically by name.
 */
export const PINNED_DIAL_CODE_ISOS: readonly string[] = ['US', 'GB'];

const pinnedRank = (d: DialCode): number => {
  const index = PINNED_DIAL_CODE_ISOS.indexOf(d.iso);
  return index === -1 ? PINNED_DIAL_CODE_ISOS.length : index;
};

/** The picker list: pinned countries first, then the rest alphabetically by name. */
export const DIAL_CODES: readonly DialCode[] = [...CURATED_DIAL_CODES].sort(
  (a, b) => pinnedRank(a) - pinnedRank(b) || a.name.localeCompare(b.name, 'en'),
);

/** Longest-first so a multi-digit code (+351) wins the prefix match over a shorter one (and +1 over none). */
export const DIAL_CODES_BY_LENGTH: readonly DialCode[] = [...DIAL_CODES].sort((a, b) => b.code.length - a.code.length);

/**
 * The dial code for an ISO-3166 alpha-2 country (e.g. `'GB'` → `'+44'`), or `undefined` if the country
 * isn't in the curated list. Used to default the mobile picker from the user's profile-address country.
 */
export function dialCodeForCountry(iso: string | undefined | null): string | undefined {
  if (!iso) return undefined;
  const upper = iso.toUpperCase();
  return DIAL_CODES.find((d) => d.iso === upper)?.code;
}

/**
 * The regional-indicator flag emoji for an ISO-3166 alpha-2 country (e.g. `'GB'` → 🇬🇧). Returns an
 * empty string for anything that isn't a 2-letter code.
 */
export function flagEmoji(iso: string): string {
  if (!/^[A-Za-z]{2}$/.test(iso)) return '';
  const OFFSET = 0x1f1e6 - 'A'.charCodeAt(0); // regional-indicator 'A'
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => c.charCodeAt(0) + OFFSET));
}

/**
 * Splits a phone string into its dial code + national number, taking the LONGEST dial code that
 * prefixes the value (so `+351…` isn't mis-split as `+3`/`+35`). Handles both the form's spaced shape
 * (`"+44 7911 123456"`) and a bare E.164 (`"+447911123456"`, e.g. a verified Firebase Auth number).
 * Falls back to the default dial code with the raw value as the national part.
 */
export function splitDialCode(value: string, defaultDialCode = '+1'): { dialCode: string; number: string } {
  const compact = value.replace(/\s+/g, '');
  if (!compact) return { dialCode: defaultDialCode, number: '' };
  if (compact.startsWith('+')) {
    const matched = DIAL_CODES_BY_LENGTH.find((d) => compact.startsWith(d.code));
    if (matched) return { dialCode: matched.code, number: compact.slice(matched.code.length) };
  }
  return { dialCode: defaultDialCode, number: value.trim() };
}
